import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  CloseIcon,
  CoreContainer,
  Notification,
  ScrollBar,
} from "../common/style";
import { Product } from "./index";
import {
  AddedProductsContainer,
  Card,
  CardGroup,
  CardImgContainer,
  CardProductUnCheck,
  CatalogsContainer,
  SearchInput,
} from "./style";
import { Button, CardImg, Spinner } from "reactstrap";
import { AppContext } from "../context";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { addProducts, getProducts } from "./service";
import { debounce } from "lodash";
import useSearchParamHandler from "../utils";

const Products = (props) => {
  const { catalogs, catalogsLoading, setCatalogs, setCatalogsLoading } =
    useContext(AppContext);
  const [searchParams] = useSearchParams();
  const replaceSearchParam = useSearchParamHandler();
  const searchValue = searchParams.get("search");
  const [searchText, setSearchText] = useState("");
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [addedProducts, setAddedProducts] = useState([]);
  const [showMore, setShowMore] = useState({});

  const outletContext = useOutletContext();
  const {
    projectId: contextProjectId,
    currentRoom: contextCurrentRoom,
    selectionManager: contextSelectionManager,
    loadRoom: contextLoadRoom
  } = outletContext || {};

  // Use props if available, otherwise use context
  const projectId = props.projectId ?? contextProjectId;
  const currentRoom = props.currentRoom ?? contextCurrentRoom;
  const selectionManager = props.selectionManager ?? contextSelectionManager;
  const loadRoom = props.loadRoom ?? contextLoadRoom;

  const addProduct = (product) => {
    const index = addedProducts.findIndex((e) => e.id == product.id);
    const newList = [...addedProducts];

    if (index > -1) newList.splice(index, 1);
    else newList.push(product);

    setAddedProducts(newList);
  };

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    debounceSearch(searchValue, searchParams);
  };

  const debouncedSearch = (searchTerm, searchParams) => {
    replaceSearchParam("search", searchTerm, searchParams);
  };

  const debounceSearch = useCallback(debounce(debouncedSearch, 500), []);
  const searchTerm = searchParams.get("search") || "";

  useEffect(() => {
    return () => {
      handleSearch("");
      setCatalogs({});
    };
  }, []);

  useEffect(() => {
    setCatalogsLoading(true);
    setSearchText(searchTerm);

    getProducts({
      searchTerm,
      spaceDimensions: props.spaceDimensions,
      category: props.modulesCategory,
      catalogType: props.catalogType,
      catalogSubType: props.catalogSubType,
      defaultCatalogs:
        props.isCurrentTab && props.isCurrentTab("catalog") && !searchTerm,
    })
      .then((products) => {
        setCatalogs(products);
        props.isCurrentTab &&
          props.isCurrentTab("catalog") &&
          setShowMore(
            Object.keys(products).reduce((acc, value) => {
              acc[value] = false;
              return acc;
            }, {})
          );
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ type: "error", message: error });
      })
      .finally(() => setCatalogsLoading(false));
  }, [
    searchTerm,
    setCatalogs,
    setCatalogsLoading,
    props.modulesCategory,
    props.selectedSpacePath,
  ]);

  const canShowModules =
    selectionManager.selectedGroup() ||
    props.modulesCategory ||
    (props.isCurrentTab && props.isCurrentTab("catalog")) ||
    (props.catalogType && props.catalogSubType);

  const getPlaceholderText = (category) => {
    if (!category)
      return "Search any module (ex: 2 door wardrobe, wall unit, tandem, etc.)";

    if (category === "external_submodule")
      return "Search submodules (e.g., 2 drawers, 3 shelves, etc. )";

    if (category === "internal_submodule")
      return "Search submodules (e.g., 2 external drawers, robe rail with shelves )";
  };

  const getMidPoint = (zoomAmount, containerRef, position, renderingSize) => {
    const rect = containerRef.current.getBoundingClientRect();
    // Needed because the height of the div extends off screen
    const visibleHeight = Math.min(rect.height, window.innerHeight - rect.top);

    const range = {
      xMin: (-1 * position.x * renderingSize) / zoomAmount,
      yMin:
        ((rect.height - visibleHeight) * renderingSize) / zoomAmount +
        (-1 * position.y * renderingSize) / zoomAmount,
      xMax: ((-1 * position.x + rect.width) * renderingSize) / zoomAmount,
      yMax: ((-1 * position.y + visibleHeight) * renderingSize) / zoomAmount,
    };

    return {
      x: (range.xMin + range.xMax) / 2,
      y: (range.yMin + range.yMax) / 2,
    };
  };

  const addModule = (products) => {
    const roomId = currentRoom.id;
    const productIds = products.map((e) => e.id);

    if (!roomId || productIds.length === 0) return;

    props.setIsRightPaneLoading(true);

    let newPosition = null;
    const position = props.position;
    const zoom = props.zoom;
    const containerRef = props.containerRef;
    const x = Math.floor((-position.x * 5 + 200) / zoom);
    const y = Math.floor((-position.y * 5 + 200) / zoom);
    const renderingSize = 5;
    newPosition = getMidPoint(zoom, containerRef, position, renderingSize);

    addProducts({
      productIds,
      projectId,
      roomId,
      position: newPosition,
    })
      .then((response) => {
        setAddedProducts([]);
        loadRoom(roomId, response.order_item_id, null);
        props.setCurrentTab("info");
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ type: "error", message: error });
      })
      .finally(() => {
        props.setIsRightPaneLoading(false);
      });
  };

  const loadCatalogs = (type) =>
    setShowMore({
      ...showMore,
      [type]: !showMore[type],
    });

  return !canShowModules ? (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      <Card className="d-flex justify-content-center align-items-center empty mt-3">
        Click on any module on the left to customise it
      </Card>
    </>
  ) : (
    <div>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      <>
        <div className="pt-3 position-relative">
          <SearchInput
            className="unmask"
            autoFocus
            type="search"
            placeholder={getPlaceholderText(props.modulesCategory)}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        {catalogsLoading ? (
          <CoreContainer>
            <Spinner>Loading...</Spinner>
          </CoreContainer>
        ) : Object.keys(catalogs).length === 0 ? (
          <Card className="d-flex justify-content-center align-items-center empty px-3 mt-3">
            {searchParams.get("search") && (
              <span>No modules found! Try a different keyword.</span>
            )}
            {!searchParams.get("search") && <span>Type to search modules</span>}
          </Card>
        ) : (
          <CatalogsContainer className="modules-container">
            {Object.entries(catalogs).map(([type, products]) => (
              <div>
                {type && <p className="ms-1 fw-bold fs-6 mb-0">{type}</p>}
                <CardGroup className="modules-listing row">
                  {(() => {
                    const filteredProducts =
                      props.isCurrentTab &&
                      props.isCurrentTab("catalog") &&
                      !showMore[type] &&
                      !searchText
                        ? products.slice(0, 4)
                        : products;

                    return filteredProducts.map((product, index) => {
                      return (
                        <Product
                          projectId={projectId}
                          currentRoom={currentRoom}
                          modulesCategory={props.modulesCategory}
                          handleCopyTemplate={props.handleCopyTemplate}
                          isSpaceShuttered={props.isSpaceShuttered}
                          key={index}
                          id={product.id}
                          images={product.images}
                          name={product.name}
                          sku={product.sku}
                          actualPrice={product.actual_price}
                          sqftPrice={product.sqft_rate}
                          tags={product.tags}
                          dimensions={product.dimensions}
                          searchValue={searchValue}
                          setSearchText={setSearchText}
                          setIsRightPaneLoading={props.setIsRightPaneLoading}
                          position={props.position}
                          zoom={props.zoom}
                          catalogType={props.catalogType}
                          catalogSubType={props.catalogSubType}
                          orderItemId={props.orderItemId}
                          changeTemplate={props.changeTemplate}
                          productDimensionsRange={product.dimensions_range}
                          product={product}
                          addedProducts={addedProducts}
                          addProduct={addProduct}
                        />
                      );
                    });
                  })()}
                </CardGroup>
                {props.isCurrentTab &&
                  props.isCurrentTab("catalog") &&
                  catalogs[type].length > 4 &&
                  !searchTerm && (
                    <div className="mt-3 d-flex justify-content-center">
                      <Button
                        className="bg-white rounded-1"
                        style={{ border: "1px solid #999", color: "#666666" }}
                        onClick={() => loadCatalogs(type)}
                      >
                        {showMore[type] ? "Show Less" : "Show More"}
                      </Button>
                    </div>
                  )}
              </div>
            ))}
          </CatalogsContainer>
        )}

        {addedProducts.length > 0 && (
          <AddedProductsContainer>
            <div className="p-3">
              <ScrollBar className="d-flex column-gap-3 align-items-center ps-2 pt-2 pb-1">
                {addedProducts.map((product) => (
                  <div className="position-relative col-2">
                    <CardProductUnCheck
                      className="d-flex"
                      onClick={() => addProduct(product)}
                    >
                      <CloseIcon />
                    </CardProductUnCheck>
                    <CardImgContainer minHeight="0px" maxHeight="80px">
                      <CardImg
                        className="object-fit-contain"
                        src={product.images[0]}
                        alt={product.name}
                      />
                    </CardImgContainer>
                  </div>
                ))}
              </ScrollBar>
            </div>
            {props.access.add_module && (
              <div className="d-flex justify-content-end p-2 pe-4 border-top">
                <Button
                  className="border-0 px-4 py-2"
                  style={{ backgroundColor: "#F11F1F" }}
                  onClick={() => addModule(addedProducts)}
                >
                  Add Modules
                </Button>
              </div>
            )}
          </AddedProductsContainer>
        )}
      </>
    </div>
  );
};

export default Products;
